/**
 * Receiving Workflow Simulator
 * Simulates the complete receiving workflow using real API calls
 */

import { getSharedRateLimiter } from '../services/RateLimiter';
import { VirtualOperator } from '../services/VirtualOperator';
import { AuthService } from '../../../../../services/authService';
import { SimulatorConfig } from '../types';

// Forward declaration to avoid circular import
interface SimulatorEngineInterface {
  isPurchaseOrderProcessed(orderNo: number): boolean;
  markPurchaseOrderProcessed(orderNo: number): void;
}

// API base URL - use environment variable for production deployments
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

// Authenticated fetch helper using AuthService
const authenticatedFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  return AuthService.authenticatedFetch(url, options);
};

export interface ReceivingWorkflowResult {
  success: boolean;
  orderNo?: number;
  batchId?: string;
  linesProcessed: number;
  itemsReceived: number;
  error?: string;
  duration: number;
}

export class ReceivingWorkflow {
  private config: SimulatorConfig;
  private operator: VirtualOperator;
  private rateLimiter = getSharedRateLimiter();
  private engine: SimulatorEngineInterface | null;

  constructor(config: SimulatorConfig, operator: VirtualOperator, engine?: SimulatorEngineInterface) {
    this.config = config;
    this.operator = operator;
    this.engine = engine || null;
  }

  /**
   * Execute one complete receiving workflow cycle
   */
  async execute(): Promise<ReceivingWorkflowResult> {
    const startTime = Date.now();
    let orderNo: number | undefined;
    let batchId: string | undefined;
    let linesProcessed = 0;
    let itemsReceived = 0;

    try {
      // Step 1: Get orders for receiving
      this.operator.startTask('receiving', 'Henter bestillinger for mottak');
      await this.rateLimiter.throttle();

      const ordersForReceiving = await this.getOrdersForReceiving();

      // Step 2: Filter out already processed orders (prevents re-processing loop)
      let availableOrders = ordersForReceiving;
      if (this.engine) {
        availableOrders = ordersForReceiving.filter((order: any) => {
          const orderNumber = order.orderNo ?? order.OrderNo ?? order.orderno;
          const isProcessed = this.engine!.isPurchaseOrderProcessed(orderNumber);
          if (isProcessed) {
            console.log(`[ReceivingWorkflow] Skipping already processed order ${orderNumber}`);
          }
          return !isProcessed;
        });

        console.log(`[ReceivingWorkflow] ${availableOrders.length} of ${ordersForReceiving.length} orders available (${ordersForReceiving.length - availableOrders.length} already processed)`);
      }

      // Step 3: Handle case when no orders are available
      if (availableOrders.length === 0) {
        // Only generate new orders if there are truly NO orders in the database (not just all processed)
        // This prevents infinite order generation loop
        if (ordersForReceiving.length === 0 && this.config.workflows.receiving.ordersToGenerate > 0) {
          this.operator.startTask('receiving', 'Genererer nye innkjøpsordrer...');
          console.log(`[ReceivingWorkflow] No orders in database - generating ${this.config.workflows.receiving.ordersToGenerate} new orders`);
          await this.rateLimiter.throttle();

          const generated = await this.generatePurchaseOrders();
          if (generated) {
            // Wait for orders to be created, then fetch again
            await this.delay(1000);
            const newOrders = await this.getOrdersForReceiving();

            // Filter out processed orders from the new list
            if (this.engine) {
              availableOrders = newOrders.filter((order: any) => {
                const orderNumber = order.orderNo ?? order.OrderNo ?? order.orderno;
                return !this.engine!.isPurchaseOrderProcessed(orderNumber);
              });
            } else {
              availableOrders = newOrders;
            }

            console.log(`[ReceivingWorkflow] After generation: ${availableOrders.length} new orders available`);
          }
        }

        // Still no orders available
        if (availableOrders.length === 0) {
          const message = ordersForReceiving.length > 0
            ? `Alle ${ordersForReceiving.length} bestillinger er allerede behandlet i denne sesjonen. Venter på nye ordrer eller restart simulatoren.`
            : 'Ingen bestillinger for mottak';
          console.log(`[ReceivingWorkflow] ${message}`);
          return {
            success: true,
            linesProcessed: 0,
            itemsReceived: 0,
            duration: Date.now() - startTime,
            error: message
          };
        }
      }

      // Step 4: Select a random order from available orders
      const selectedOrder = availableOrders[Math.floor(Math.random() * availableOrders.length)];
      orderNo = selectedOrder.orderNo ?? selectedOrder.OrderNo ?? selectedOrder.orderno;

      if (orderNo === undefined) {
        return {
          success: false,
          linesProcessed: 0,
          itemsReceived: 0,
          duration: Date.now() - startTime,
          error: 'Kunne ikke finne orderNo i valgt bestilling'
        };
      }

      this.operator.startTask('receiving', `Behandler bestilling ${orderNo}`);
      await this.rateLimiter.throttle();

      // Step 3: Get order details with lines
      const orderDetails = await this.getOrderDetails(orderNo);
      const lines = orderDetails.lines || [];

      if (lines.length === 0) {
        return {
          success: true,
          orderNo,
          linesProcessed: 0,
          itemsReceived: 0,
          duration: Date.now() - startTime,
          error: 'Ingen linjer å motta'
        };
      }

      // Check if any lines have remaining quantity to receive
      const linesToReceive = lines.filter((line: any) => {
        const qtyOrdered = line.quantity || line.qty || 0;
        const qtyDelivered = line.quantityDelivered || line.qtyReceived || 0;
        return qtyOrdered > qtyDelivered;
      });

      if (linesToReceive.length === 0) {
        console.log(`[ReceivingWorkflow] Order ${orderNo} has no lines with remaining quantity - all items already received`);
        return {
          success: true,
          orderNo,
          linesProcessed: 0,
          itemsReceived: 0,
          duration: Date.now() - startTime,
          error: 'Alle linjer er allerede mottatt'
        };
      }

      console.log(`[ReceivingWorkflow] Order ${orderNo} has ${linesToReceive.length} lines with remaining quantity to receive`);

      // Step 4: Get available bins for the warehouse
      const warehouseForReceiving = orderDetails.warehouse || this.config.warehouseId;
      this.operator.startTask('receiving', `Henter tilgjengelige lokasjoner for lager ${warehouseForReceiving}`);
      await this.rateLimiter.throttle();

      const availableBins = await this.getAvailableBins(warehouseForReceiving);
      if (availableBins.length === 0) {
        return {
          success: false,
          orderNo,
          linesProcessed: 0,
          itemsReceived: 0,
          duration: Date.now() - startTime,
          error: `Ingen tilgjengelige lokasjoner funnet for lager ${warehouseForReceiving}. Opprett minst én lokasjon i lageret.`
        };
      }

      // Use the first available bin (prioritized by getAvailableBins)
      const receivingBin = availableBins[0];
      console.log(`[ReceivingWorkflow] Using receiving bin: ${receivingBin} (from ${availableBins.length} available bins)`);

      // Step 5: Create or get existing StcTransOrder (batch)
      batchId = `SIM-RCV-${orderNo}-${Date.now()}`;

      this.operator.startTask('receiving', `Oppretter mottaksordre ${batchId}`);
      await this.rateLimiter.throttle();
      await this.createTransOrder(batchId, orderNo, warehouseForReceiving);

      // Determine if this receiving should use direct receipt (skip grunnlag)
      const useDirectReceipt = this.shouldUseDirectReceipt();
      if (useDirectReceipt) {
        console.log(`[ReceivingWorkflow] Using DIRECT RECEIPT mode for order ${orderNo} (skip grunnlag)`);
      } else {
        console.log(`[ReceivingWorkflow] Using GRUNNLAG mode for order ${orderNo} (standard put-away flow)`);
      }

      // Step 6: Process each line - receive items
      let lineId = 1; // Track line IDs for direct receipt
      for (const line of lines) {
        // Skip already fully received lines
        // Note: API returns 'quantity' and 'quantityDelivered' (camelCase from C# DTO)
        const qtyOrdered = line.quantity || line.qty || 0;
        const qtyDelivered = line.quantityDelivered || line.qtyReceived || 0;
        const qtyToReceive = qtyOrdered - qtyDelivered;

        console.log(`[ReceivingWorkflow] Line ${line.lineNo}: ordered=${qtyOrdered}, delivered=${qtyDelivered}, toReceive=${qtyToReceive}`);

        if (qtyToReceive <= 0) {
          console.log(`[ReceivingWorkflow] Skipping line ${line.lineNo} - already fully received`);
          continue;
        }

        // Simulate physical receiving process
        await this.delay(this.config.timing.receiveTimeMs);

        if (useDirectReceipt) {
          // DIRECT RECEIPT MODE: Place items directly in storage bin
          const targetBin = this.selectRandomStorageBin(availableBins);
          if (!targetBin) {
            console.warn(`[ReceivingWorkflow] No storage bin available for direct receipt, falling back to grunnlag`);
            // Fall through to grunnlag mode for this line
          } else {
            this.operator.startTask('receiving', `Direktemottak linje ${line.lineNo}: ${line.itemNo} -> ${targetBin}`);

            // Create transaction for received items
            await this.rateLimiter.throttle();
            const transResult = await this.createTransaction(batchId, {
              itemNo: line.itemNo,
              quantity: qtyToReceive,
              orderNo,
              lineNo: line.lineNo,
              description1: line.description1,
              locationName: receivingBin,
              warehouse: warehouseForReceiving
            });

            // Process direct receipt - this updates ItemBins directly
            await this.rateLimiter.throttle();
            const directReceiptSuccess = await this.processDirectReceipt(
              batchId,
              lineId, // Use sequential line ID
              targetBin,
              qtyToReceive
            );

            if (directReceiptSuccess) {
              console.log(`[ReceivingWorkflow] Direct receipt completed: ${line.itemNo} x ${qtyToReceive} -> ${targetBin}`);
            } else {
              console.warn(`[ReceivingWorkflow] Direct receipt failed, item may need manual handling`);
            }

            linesProcessed++;
            itemsReceived += qtyToReceive;
            lineId++;
            continue; // Skip grunnlag creation
          }
        }

        // GRUNNLAG MODE: Standard receiving with put-away later
        this.operator.startTask('receiving', `Mottar linje ${line.lineNo}: ${line.itemNo} til ${receivingBin}`);

        // Create transaction for received items
        await this.rateLimiter.throttle();
        await this.createTransaction(batchId, {
          itemNo: line.itemNo,
          quantity: qtyToReceive,
          orderNo,
          lineNo: line.lineNo,
          description1: line.description1,
          locationName: receivingBin,
          warehouse: warehouseForReceiving
        });

        // Create StcIntRelocation record (Grunnlag for put-away)
        // This enables the put-away workflow to work
        try {
          await this.rateLimiter.throttle();
          await this.createRelocationRecord({
            itemNo: line.itemNo,
            quantity: qtyToReceive,
            binCode: receivingBin,
            orderNo,
            lineNo: line.lineNo,
            transType: 1 // TransType 1 = Put-away
          });
          console.log(`[ReceivingWorkflow] Created grunnlag for ${line.itemNo} x ${qtyToReceive} at ${receivingBin}`);
        } catch (relError) {
          console.warn(`[ReceivingWorkflow] Could not create relocation record for line ${line.lineNo}: ${relError}`);
          // Continue - relocation record is optional for receiving to complete
        }

        linesProcessed++;
        itemsReceived += qtyToReceive;
        lineId++;
      }

      // Step 7: Start receiving process
      this.operator.startTask('receiving', `Starter mottak for ${batchId}`);
      await this.rateLimiter.throttle();
      await this.startReceiving(batchId);

      // Step 8: Finish receiving
      this.operator.startTask('receiving', `Avslutter mottak for ${batchId}`);
      await this.rateLimiter.throttle();
      await this.finishReceiving(batchId);

      // Mark order as processed to prevent re-processing
      if (this.engine && orderNo !== undefined) {
        this.engine.markPurchaseOrderProcessed(orderNo);
        console.log(`[ReceivingWorkflow] Successfully completed receiving for order ${orderNo}`);
      }

      return {
        success: true,
        orderNo,
        batchId,
        linesProcessed,
        itemsReceived,
        duration: Date.now() - startTime
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ukjent feil';
      console.error(`[ReceivingWorkflow] Error:`, errorMessage);

      return {
        success: false,
        orderNo,
        batchId,
        linesProcessed,
        itemsReceived,
        duration: Date.now() - startTime,
        error: errorMessage
      };
    }
  }

  /**
   * Get orders for receiving
   */
  private async getOrdersForReceiving(): Promise<any[]> {
    const response = await authenticatedFetch(`${API_BASE_URL}/porders/receiving`);

    if (!response.ok) {
      throw new Error(`Failed to get orders for receiving: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get order details with lines
   */
  private async getOrderDetails(orderNo: number): Promise<any> {
    const response = await authenticatedFetch(`${API_BASE_URL}/porders/${orderNo}/details`);

    if (!response.ok) {
      throw new Error(`Failed to get order details: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a new StcTransOrder (receiving batch)
   */
  private async createTransOrder(batchId: string, orderNo: number, warehouse: string): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans`,
      {
        method: 'POST',
        body: JSON.stringify({
          batchID: batchId,
          operatorID: this.operator.getId(),
          orderDate: new Date().toISOString(),
          description1: `Simulator mottak for ordre ${orderNo}`,
          orderStatus: 0,
          orderType: 1, // Receiving
          warehouse: warehouse, // IMPORTANT: Must be set for finish-receiving to work
          startWorkDateTime: new Date().toISOString()
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create trans order: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Create a transaction (receiving line)
   */
  private async createTransaction(batchId: string, data: {
    itemNo: string;
    quantity: number;
    orderNo: number;
    lineNo: number;
    description1?: string;
    locationName?: string;
    warehouse?: string;
  }): Promise<any> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions`,
      {
        method: 'POST',
        body: JSON.stringify({
          batchID: batchId,
          itemNo: data.itemNo,
          quantity: data.quantity,
          orderNo: data.orderNo,
          lineNo: data.lineNo,
          description1: data.description1,
          locationName: data.locationName,
          warehouse: data.warehouse || this.config.warehouseId,
          operatorID: this.operator.getId(),
          lineType: 0, // LineType 0 = Required for POrderLine.QuantityDelivered trigger logic
          transType: 20, // TransType 20 = Varemottak (for transaction history)
          lineStatus: 20 // LineStatus 20 = UnderArbeid/Ankommet - required for finish-receiving
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create transaction: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Start receiving process
   */
  private async startReceiving(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/start-receiving`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to start receiving: ${response.statusText}`);
    }
  }

  /**
   * Finish receiving
   */
  private async finishReceiving(batchId: string): Promise<void> {
    const encodedBatchId = encodeURIComponent(batchId);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/stocktrans/${encodedBatchId}/finish-receiving`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to finish receiving: ${response.statusText}`);
    }
  }

  /**
   * Create StcIntRelocation record (Grunnlag for put-away)
   * This is the basis for creating put-away orders
   */
  private async createRelocationRecord(data: {
    itemNo: string;
    quantity: number;
    binCode: string;
    orderNo: number;
    lineNo: number;
    transType: number;
  }): Promise<any> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/intrelocations`,
      {
        method: 'POST',
        body: JSON.stringify({
          WarehouseID: this.config.warehouseId,
          ItemNo: data.itemNo,
          BinCode: data.binCode,
          Quantity: data.quantity,
          TransType: data.transType,
          TransDt: new Date().toISOString(),
          LineStatus: 20, // Pending
          OperatorID: this.operator.getId(),
          OrderNo: data.orderNo,
          LineNo: data.lineNo
        })
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(error.error || `Failed to create relocation record: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Generate purchase orders when none are available for receiving
   */
  private async generatePurchaseOrders(): Promise<boolean> {
    try {
      const ordersToGenerate = this.config.workflows.receiving.ordersToGenerate;
      console.log(`[ReceivingWorkflow] Generating ${ordersToGenerate} purchase orders...`);

      // Generate random purchase orders
      const response = await authenticatedFetch(
        `${API_BASE_URL}/porders/generate-random`,
        {
          method: 'POST',
          body: JSON.stringify({
            numberOfOrders: ordersToGenerate,
            numberOfLines: Math.floor(Math.random() * 3) + 2, // 2-4 lines per order
            allowItemsWithoutStock: true, // Allow items that don't have stock yet
            warehouse: this.config.warehouseId,
            maxQuantityPerLine: 50
          })
        }
      );

      if (!response.ok) {
        console.warn('[ReceivingWorkflow] Failed to generate purchase orders:', response.statusText);
        return false;
      }

      const result = await response.json();
      console.log('[ReceivingWorkflow] Successfully generated purchase orders:', result);
      return true;

    } catch (error) {
      console.error('[ReceivingWorkflow] Error generating purchase orders:', error);
      return false;
    }
  }

  /**
   * Get available bins for the warehouse
   * Returns bins sorted by BinCode, prioritizing common receiving bins like "MOTTAK", "1", "INN" etc.
   */
  private async getAvailableBins(warehouseId: string): Promise<string[]> {
    try {
      // Use the StcBinController endpoint: /api/v1/stc-bins/company/{companyNo}/warehouse/{warehouseID}
      const companyNo = this.config.companyNo || '001';
      const response = await authenticatedFetch(
        `${API_BASE_URL}/stc-bins/company/${encodeURIComponent(companyNo)}/warehouse/${encodeURIComponent(warehouseId)}`
      );

      if (!response.ok) {
        console.warn('[ReceivingWorkflow] Could not get bins:', response.status, response.statusText);
        return [];
      }

      const bins = await response.json();

      // Extract bin codes and sort, prioritizing common receiving bins
      const receivingBinPriority = ['MOTTAK', 'INN', 'RECEIVING', 'RCV', '1', '001'];
      const binCodes = (bins as any[])
        .map(b => b.binCode || b.BinCode)
        .filter(Boolean)
        .sort((a, b) => {
          const aIdx = receivingBinPriority.findIndex(p => a.toUpperCase().includes(p));
          const bIdx = receivingBinPriority.findIndex(p => b.toUpperCase().includes(p));

          if (aIdx >= 0 && bIdx >= 0) return aIdx - bIdx;
          if (aIdx >= 0) return -1;
          if (bIdx >= 0) return 1;
          return a.localeCompare(b);
        });

      console.log(`[ReceivingWorkflow] Found ${binCodes.length} bins for warehouse ${warehouseId}: ${binCodes.slice(0, 5).join(', ')}${binCodes.length > 5 ? '...' : ''}`);
      return binCodes;
    } catch (error) {
      console.error('[ReceivingWorkflow] Error fetching bins:', error);
      return [];
    }
  }

  /**
   * Process direct receipt for a transaction line
   * This skips the grunnlag/put-away process and places items directly in the target bin
   */
  private async processDirectReceipt(
    batchId: string,
    lineId: number,
    locationCode: string,
    quantity: number
  ): Promise<boolean> {
    try {
      const encodedBatchId = encodeURIComponent(batchId);

      const response = await authenticatedFetch(
        `${API_BASE_URL}/stocktrans/${encodedBatchId}/transactions/${lineId}/direct-receipt`,
        {
          method: 'POST',
          body: JSON.stringify({
            locationCode: locationCode,
            quantity: quantity
          })
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(() => ({ errorMessage: response.statusText }));
        console.warn(`[ReceivingWorkflow] Direct receipt failed for line ${lineId}: ${error.errorMessage || error.error}`);
        return false;
      }

      const result = await response.json();
      console.log(`[ReceivingWorkflow] Direct receipt successful for line ${lineId} -> ${locationCode}`);
      return result.success !== false;

    } catch (error) {
      console.error(`[ReceivingWorkflow] Error processing direct receipt for line ${lineId}:`, error);
      return false;
    }
  }

  /**
   * Select a random bin from available bins (excluding receiving bins)
   * Used for direct receipt to select a target storage location
   */
  private selectRandomStorageBin(availableBins: string[]): string | null {
    // Filter out typical receiving bins - we want storage bins
    const storageBins = availableBins.filter(bin => {
      const upperBin = bin.toUpperCase();
      return !upperBin.includes('MOTTAK') &&
             !upperBin.includes('INN') &&
             !upperBin.includes('RECEIVING') &&
             !upperBin.includes('RCV');
    });

    if (storageBins.length === 0) {
      // Fallback to any bin if no storage bins found
      if (availableBins.length > 0) {
        return availableBins[Math.floor(Math.random() * availableBins.length)];
      }
      return null;
    }

    return storageBins[Math.floor(Math.random() * storageBins.length)];
  }

  /**
   * Determine if this receiving should use direct receipt (skip grunnlag)
   * Returns true randomly ~30% of the time
   */
  private shouldUseDirectReceipt(): boolean {
    return Math.random() < 0.3; // 30% chance of direct receipt
  }

  /**
   * Delay helper
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default ReceivingWorkflow;
